import React, { useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  TextField,
  Avatar,
  Stack,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  MenuItem,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate, useParams } from "react-router-dom";
import { getHeader, toastError } from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { MuiTelInput } from "mui-tel-input";
import { API_ENDPOINT } from "../../utils/constants";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import useGetDesignations from "../../hooks/useGetDesignations";
import BackBtn from "../../widgets/BackBtn";
import { useFormik } from "formik";
import * as Yup from "yup";

const AddStaff = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [upLoading, setUpLoading] = useState(false);

  const { data: designationS } = useGetDesignations();

  // Validation schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    employeeId: Yup.string()
      .matches(
        /^[A-Z0-9-]*$/,
        "Employee ID can only contain uppercase letters, numbers, and hyphens"
      )
      .max(20, "Employee ID must be at most 20 characters"),
    phone: Yup.string()
      .matches(/^\+?[\d\s-]{0,15}$/, "Invalid phone number format")
      .nullable(),
    designation: Yup.string().required("Designation is required"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      employeeId: "",
      phone: "",
      designation: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!selectedFile) {
        toast.error("Profile image is required");
        return;
      }

      try {
        setUpLoading(true);
        const formData = new FormData();
        formData.append("image", selectedFile);
        formData.append("name", values.name);
        formData.append("email", values.email);
        if (values.employeeId) formData.append("employeeId", values.employeeId);
        if (values.phone) formData.append("phone", values.phone);
        formData.append("designation", values.designation);
        formData.append("password", values.password);

        const { data } = await axios.post(
          `${API_ENDPOINT}org/add-member-manually?type=${type}`,
          formData,
          getHeader()
        );
        toast.success(data.message);
        setTimeout(() => {
          //navigate(`/visitor-profile/${data.visitorToken}`);
        }, 100);
      } catch (error) {
        toastError(error);
      } finally {
        setUpLoading(false);
      }
    },
  });

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setProfileImage(URL.createObjectURL(file));
    }
  };

  return (
    <Container>
      <BackBtn />
      <Container maxWidth="sm">
        <Card sx={{ mt: 2, p: 3, textAlign: "center", boxShadow: 3 }}>
          <CardContent>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{ mt: 4 }}
            >
              Add {type} manually
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              <Stack spacing={2} alignItems="center">
                <Avatar src={profileImage} sx={{ width: 100, height: 100 }}>
                  {!profileImage && "?"}
                </Avatar>
                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ borderRadius: 20 }}
                >
                  Upload Profile Image *
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Stack>

              <TextField
                required
                size="small"
                fullWidth
                label="Name"
                variant="outlined"
                sx={{ mt: 3 }}
                {...formik.getFieldProps("name")}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />

              <TextField
                required
                size="small"
                fullWidth
                label="Email"
                variant="outlined"
                sx={{ mt: 3 }}
                {...formik.getFieldProps("email")}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />

              <TextField
                size="small"
                fullWidth
                label="Employee ID"
                variant="outlined"
                sx={{ mt: 3 }}
                {...formik.getFieldProps("employeeId")}
                error={
                  formik.touched.employeeId && Boolean(formik.errors.employeeId)
                }
                helperText={
                  formik.touched.employeeId && formik.errors.employeeId
                }
              />

              <MuiTelInput
                sx={{ mt: 3 }}
                fullWidth
                size="small"
                label="Mobile No (Optional)"
                defaultCountry="IN"
                forceCallingCode
                value={formik.values.phone}
                onChange={(value) => formik.setFieldValue("phone", value)}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />

              <TextField
                fullWidth
                select
                required
                size="small"
                label="Designation"
                variant="outlined"
                sx={{ mt: 3 }}
                {...formik.getFieldProps("designation")}
                error={
                  formik.touched.designation &&
                  Boolean(formik.errors.designation)
                }
                helperText={
                  formik.touched.designation && formik.errors.designation
                }
              >
                {designationS?.map((option) => (
                  <MenuItem key={option._id} value={option.designation}>
                    {option.designation}
                  </MenuItem>
                ))}
              </TextField>

              <FormControl
                variant="outlined"
                size="small"
                required
                fullWidth
                sx={{ mt: 3 }}
              >
                <InputLabel htmlFor="standard-adornment-password">
                  Password
                </InputLabel>
                <OutlinedInput
                  id="standard-adornment-password"
                  type={showPassword ? "text" : "password"}
                  {...formik.getFieldProps("password")}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(event) => event.preventDefault()}
                      >
                        {!showPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                />
                {formik.touched.password && formik.errors.password && (
                  <Typography color="error" variant="caption">
                    {formik.errors.password}
                  </Typography>
                )}
              </FormControl>

              <LoadingButton
                fullWidth
                loading={upLoading}
                variant="contained"
                sx={{ mt: 3, borderRadius: 20, py: 1.5 }}
                type="submit"
              >
                Add Staff
              </LoadingButton>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Container>
  );
};

export default AddStaff;
